import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl } from './api';
import { orderRefLabel } from './orderRef';
import { PresenceStatusDot } from './avatarPresence';
import { EmptyState } from './EmptyState';
import { SkeletonInboxList } from './SkeletonCard';
import './MessagesInbox.css';

export type InboxThread = {
  id: string;
  kind: 'response' | 'chat';
  is_active: boolean;
  is_archived?: boolean;
  order_id: number;
  order_title: string;
  order_status?: string;
  label: string;
  is_service_order?: boolean;
  response_id?: number | null;
  peer: {
    id: number;
    name: string;
    first_name?: string | null;
    last_name?: string | null;
    avatar_url?: string | null;
    last_seen_at?: string | null;
  };
  last_message: {
    id?: number | null;
    body?: string;
    created_at?: string;
    is_mine?: boolean;
  };
  unread_count: number;
};

type InboxTab = 'active' | 'all' | 'archive';

type Props = {
  token: string;
  onOpenThread: (thread: InboxThread) => void;
  onOpenPeer: (userId: number) => void;
  onDeleted?: () => void;
};

const DELETE_W = 88;
const OPEN_THRESHOLD = 48;

function formatWhen(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

function SwipeInboxRow({
  thread,
  open,
  onOpenChange,
  onOpenThread,
  onOpenPeer,
  onDelete
}: {
  thread: InboxThread;
  open: boolean;
  onOpenChange: (id: string | null) => void;
  onOpenThread: (thread: InboxThread) => void;
  onOpenPeer: (userId: number) => void;
  onDelete: (thread: InboxThread) => void;
}) {
  const unread = Number(thread.unread_count) || 0;
  const initial = (thread.peer.first_name || thread.peer.name || '?').slice(0, 1).toUpperCase();
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const startOffset = useRef(0);
  const axis = useRef<'x' | 'y' | null>(null);
  const moved = useRef(false);
  const offsetRef = useRef(0);
  const rowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    if (!dragging) setOffset(open ? -DELETE_W : 0);
  }, [open, dragging]);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      startX.current = t.clientX;
      startY.current = t.clientY;
      startOffset.current = open ? -DELETE_W : 0;
      axis.current = null;
      moved.current = false;
      setDragging(true);
    };

    const onMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX.current;
      const dy = t.clientY - startY.current;
      if (axis.current == null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        axis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (axis.current !== 'x') return;
      moved.current = true;
      e.preventDefault();
      const next = Math.min(0, Math.max(-DELETE_W, startOffset.current + dx));
      offsetRef.current = next;
      setOffset(next);
    };

    const onEnd = () => {
      setDragging(false);
      if (axis.current !== 'x') {
        axis.current = null;
        return;
      }
      const shouldOpen = offsetRef.current <= -OPEN_THRESHOLD;
      setOffset(shouldOpen ? -DELETE_W : 0);
      onOpenChange(shouldOpen ? thread.id : null);
      axis.current = null;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [open, onOpenChange, thread.id]);

  const onRowClick = () => {
    if (moved.current) {
      moved.current = false;
      return;
    }
    if (open) {
      onOpenChange(null);
      return;
    }
    onOpenThread(thread);
  };

  return (
    <div className={'fc-inbox-swipe' + (open ? ' fc-inbox-swipe--open' : '')}>
      <button
        type="button"
        className="fc-inbox-swipe__delete"
        aria-label="Удалить чат"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(thread);
        }}
      >
        Удалить
      </button>
      <div
        ref={rowRef}
        className={
          'fc-inbox-row' +
          (unread > 0 ? ' fc-inbox-row--unread' : '') +
          (dragging ? ' fc-inbox-row--dragging' : '')
        }
        role="button"
        tabIndex={0}
        style={{ transform: `translateX(${offset}px)` }}
        onClick={onRowClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRowClick();
          }
        }}
      >
        <span
          className="fc-inbox-row__avatar-wrap"
          onClick={(e) => {
            e.stopPropagation();
            onOpenPeer(thread.peer.id);
          }}
        >
          {thread.peer.avatar_url ? (
            <img src={thread.peer.avatar_url} alt="" className="fc-inbox-row__avatar" />
          ) : (
            <span className="fc-inbox-row__avatar fc-inbox-row__avatar--ph">{initial}</span>
          )}
          <PresenceStatusDot lastSeenAt={thread.peer.last_seen_at} />
        </span>

        <span className="fc-inbox-row__main">
          <span className="fc-inbox-row__top">
            <span className="fc-inbox-row__name">{thread.peer.name}</span>
            <span className="fc-inbox-row__meta">
              <span className="fc-inbox-row__time">{formatWhen(thread.last_message?.created_at)}</span>
              {unread > 0 ? (
                <span className="fc-inbox-row__badge">{unread > 99 ? '99+' : unread}</span>
              ) : null}
            </span>
          </span>
          <span className="fc-inbox-row__order">
            <span className="fc-inbox-row__label">{thread.label}</span>
            <span className="fc-inbox-row__title">{thread.order_title}</span>
            <span className="fc-inbox-row__ref">{orderRefLabel(thread.order_id)}</span>
          </span>
          <span className="fc-inbox-row__preview">
            {thread.last_message?.is_mine ? 'Вы: ' : ''}
            {thread.last_message?.body || '—'}
          </span>
        </span>
      </div>
    </div>
  );
}

export default function MessagesInbox({ token, onOpenThread, onOpenPeer, onDeleted }: Props) {
  const [tab, setTab] = useState<InboxTab>('active');
  const [threads, setThreads] = useState<InboxThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openSwipeId, setOpenSwipeId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [allUnread, setAllUnread] = useState(0);
  const [archiveUnread, setArchiveUnread] = useState(0);

  const refreshUnreadBadges = useCallback(async () => {
    const counts = await fetchInboxUnreadBreakdown(token);
    setAllUnread(counts.all);
    setArchiveUnread(counts.archive);
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r] = await Promise.all([
        fetch(apiUrl(`/api/messages/inbox?tab=${tab}`), {
          headers: { Authorization: `Bearer ${token}` }
        }),
        refreshUnreadBadges()
      ]);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `Ошибка ${r.status}`);
      setThreads(Array.isArray(d.threads) ? d.threads : []);
      setOpenSwipeId(null);
    } catch (e: any) {
      setThreads([]);
      setError(e?.message || 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  }, [token, tab, refreshUnreadBadges]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const t = window.setInterval(() => void load(), 20000);
    return () => window.clearInterval(t);
  }, [load]);

  const confirmDelete = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      try {
        const tg = (window as unknown as { Telegram?: { WebApp?: { showConfirm?: (m: string, cb: (ok: boolean) => void) => void } } })
          .Telegram?.WebApp;
        if (tg && typeof tg.showConfirm === 'function') {
          tg.showConfirm(message, (ok: boolean) => resolve(!!ok));
          return;
        }
      } catch {
        /* ignore */
      }
      resolve(window.confirm(message));
    });
  }, []);

  const hideThread = useCallback(
    async (thread: InboxThread) => {
      if (busyId) return;
      const peerName = thread.peer?.name || 'собеседником';
      const ok = await confirmDelete(`Вы точно хотите удалить этот чат с ${peerName}?`);
      if (!ok) return;

      setBusyId(thread.id);
      const prev = threads;
      setThreads((list) => list.filter((x) => x.id !== thread.id));
      setOpenSwipeId(null);
      try {
        const r = await fetch(apiUrl(`/api/messages/inbox/${thread.order_id}/hide`), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ peer_id: thread.peer.id })
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || `Ошибка ${r.status}`);
        onDeleted?.();
        void refreshUnreadBadges();
      } catch (e: any) {
        setThreads(prev);
        setError(e?.message || 'Не удалось удалить');
      } finally {
        setBusyId(null);
      }
    },
    [busyId, threads, token, onDeleted, confirmDelete, refreshUnreadBadges]
  );

  const emptyHint =
    tab === 'active'
      ? 'Пока нет активных переписок. Чат появится, когда по заказу начнётся диалог.'
      : tab === 'archive'
        ? 'В архиве появятся переписки по завершённым заказам и услугам.'
        : 'Пока пусто. Диалог появится после отклика или сообщения в чате сделки.';

  return (
    <div className="fc-inbox">
      <div className="fc-inbox__head">
        <h1 className="fc-inbox__title">Сообщения</h1>
        <div className="fc-inbox__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'active'}
            className={'fc-inbox__tab' + (tab === 'active' ? ' fc-inbox__tab--on' : '')}
            onClick={() => setTab('active')}
          >
            Активные
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'all'}
            className={'fc-inbox__tab' + (tab === 'all' ? ' fc-inbox__tab--on' : '')}
            onClick={() => setTab('all')}
          >
            <span className="fc-inbox__tab-label">
              Все
              {allUnread > 0 ? (
                <span className="fc-inbox__tab-badge">{allUnread > 99 ? '99+' : allUnread}</span>
              ) : null}
            </span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'archive'}
            className={'fc-inbox__tab' + (tab === 'archive' ? ' fc-inbox__tab--on' : '')}
            onClick={() => setTab('archive')}
          >
            <span className="fc-inbox__tab-label">
              Архив
              {archiveUnread > 0 ? (
                <span className="fc-inbox__tab-badge">{archiveUnread > 99 ? '99+' : archiveUnread}</span>
              ) : null}
            </span>
          </button>
        </div>
      </div>

      {loading ? <SkeletonInboxList /> : null}
      {error ? <p className="fc-inbox__error">{error}</p> : null}
      {!loading && !error && threads.length === 0 ? (
        <EmptyState
          icon={tab === 'archive' ? '📦' : '💬'}
          title="Здесь пока пусто"
          subtitle={emptyHint}
          className="fc-inbox__empty"
        />
      ) : null}

      <div className="fc-inbox__list">
        {threads.map((t) => (
          <SwipeInboxRow
            key={t.id}
            thread={t}
            open={openSwipeId === t.id}
            onOpenChange={setOpenSwipeId}
            onOpenThread={onOpenThread}
            onOpenPeer={onOpenPeer}
            onDelete={(th) => void hideThread(th)}
          />
        ))}
      </div>
    </div>
  );
}

export type InboxUnreadBreakdown = {
  total: number;
  all: number;
  archive: number;
};

/** Непрочитанные: total — для нижнего таба; all / archive — для вкладок inbox */
export async function fetchInboxUnreadBreakdown(token: string): Promise<InboxUnreadBreakdown> {
  try {
    const r = await fetch(apiUrl('/api/messages/inbox/unread-count'), {
      headers: { Authorization: `Bearer ${token}` }
    });
    const d = await r.json();
    if (!r.ok) return { total: 0, all: 0, archive: 0 };
    const total = Math.max(0, Number(d.unread_total) || 0);
    const all = Math.max(0, Number(d.unread_all ?? d.unread_total) || 0);
    const archive = Math.max(0, Number(d.unread_archive) || 0);
    return { total, all, archive };
  } catch {
    return { total: 0, all: 0, archive: 0 };
  }
}

/** Короткий запрос суммы непрочитанных для badge нижнего таба */
export async function fetchInboxUnreadTotal(token: string): Promise<number> {
  const counts = await fetchInboxUnreadBreakdown(token);
  return counts.total;
}

export async function markInboxThreadRead(
  token: string,
  orderId: number,
  peerId?: number | null
): Promise<void> {
  try {
    await fetch(apiUrl(`/api/messages/inbox/${orderId}/read`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(peerId != null ? { peer_id: peerId } : {})
    });
  } catch {
    /* ignore */
  }
}
